// Inline click-to-edit system for the public site.
//
// When the admin is logged in, a floating "Edit Site" pill appears in the bottom
// corner of every public page. Toggling it on turns every <EditableText/> wrapper
// into a click target — clicking opens a small inline popover with a textarea and
// Save / Cancel buttons. Saving PUTs the merged override to /api/admin/site-config
// so the change goes live for everyone immediately.

import { createContext, useContext, useEffect, useState, useRef } from "react";
import { Pencil, Check, X, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { adminClient, getAdminToken } from "@/lib/api";
import { useSiteCfg, useSiteText } from "@/lib/siteConfig";

const EditModeContext = createContext({ on: false, setOn: () => {} });

export function EditModeProvider({ children }) {
    const [on, setOn] = useState(false);
    return (
        <EditModeContext.Provider value={{ on, setOn }}>{children}</EditModeContext.Provider>
    );
}

export function useEditMode() {
    return useContext(EditModeContext);
}

// path syntax supports object keys + numeric indices, e.g. "about.paragraphs.0"
function setPath(obj, path, value) {
    const parts = path.split(".");
    const root = Array.isArray(obj) ? [...obj] : { ...(obj || {}) };
    let cur = root;
    for (let i = 0; i < parts.length - 1; i++) {
        const p = parts[i];
        const nextIsIndex = /^\d+$/.test(parts[i + 1]);
        const existing = cur[p];
        if (Array.isArray(existing)) {
            cur[p] = [...existing];
        } else if (existing && typeof existing === "object") {
            cur[p] = { ...existing };
        } else {
            cur[p] = nextIsIndex ? [] : {};
        }
        cur = cur[p];
    }
    cur[parts[parts.length - 1]] = value;
    return root;
}

/**
 * <EditableText path="hero.title" defaultValue="Limit Break Performance"
 *               as="h1" className="..." multiline />
 *
 * Renders the live value. When edit mode is on (admin only), wraps it in a
 * click-to-edit affordance.
 */
export function EditableText({
    path,
    defaultValue,
    as: Tag = "span",
    className = "",
    multiline = false,
    children,
    placeholder,
    ...rest
}) {
    const { cfg, setCfg } = useSiteCfg();
    const { on } = useEditMode();
    const value = useSiteText(path, defaultValue);
    const [editing, setEditing] = useState(false);
    const [draft, setDraft] = useState("");
    const [saving, setSaving] = useState(false);
    const popoverRef = useRef(null);

    useEffect(() => {
        if (!editing) return;
        const onDoc = (e) => {
            if (popoverRef.current && !popoverRef.current.contains(e.target)) {
                setEditing(false);
            }
        };
        document.addEventListener("mousedown", onDoc);
        return () => document.removeEventListener("mousedown", onDoc);
    }, [editing]);

    const startEdit = (e) => {
        if (!on) return;
        e.stopPropagation();
        e.preventDefault();
        setDraft(value == null ? "" : String(value));
        setEditing(true);
    };

    const save = async () => {
        const trimmed = draft;
        const newCfg = setPath(cfg, path, trimmed);
        setSaving(true);
        try {
            await adminClient().put("/admin/site-config", newCfg);
            setCfg(newCfg);
            toast.success("Saved.");
            setEditing(false);
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Save failed.");
        } finally {
            setSaving(false);
        }
    };

    const cancel = () => setEditing(false);

    // Not in admin edit mode → render plain text.
    if (!on) {
        return (
            <Tag className={className} {...rest}>
                {value}
                {children}
            </Tag>
        );
    }

    const baseEditClass =
        "relative inline-block outline outline-1 outline-dashed outline-red-500/60 hover:outline-red-400 cursor-pointer rounded-sm";

    return (
        <Tag
            className={`${className} ${baseEditClass}`}
            onClick={startEdit}
            data-editable-path={path}
            data-testid={`editable-${path}`}
            {...rest}
        >
            {value}
            {children}
            <Pencil
                className="absolute -top-3 -right-3 w-4 h-4 text-red-500 bg-zinc-900 rounded-full p-0.5 border border-red-500/60"
                aria-hidden
            />
            {editing && (
                <span
                    ref={popoverRef}
                    onClick={(e) => e.stopPropagation()}
                    className="absolute z-[10000] left-0 top-full mt-2 min-w-[280px] max-w-[420px] bg-zinc-950 border border-red-500/40 rounded-sm p-3 shadow-2xl text-left block"
                    style={{ fontSize: "12px", fontFamily: "var(--font-body, system-ui, sans-serif)", textTransform: "none", letterSpacing: 0, lineHeight: 1.4 }}
                    data-testid={`editable-popover-${path}`}
                >
                    <span className="block text-[10px] uppercase tracking-widest text-zinc-500 mb-1">
                        Editing · <span className="text-red-400">{path}</span>
                    </span>
                    {multiline ? (
                        <textarea
                            autoFocus
                            value={draft}
                            onChange={(e) => setDraft(e.target.value)}
                            rows={4}
                            placeholder={placeholder}
                            className="w-full bg-black border border-white/10 rounded-sm px-2 py-1.5 text-white placeholder:text-zinc-600 focus:outline-none focus:border-red-500 resize-y block"
                        />
                    ) : (
                        <input
                            autoFocus
                            value={draft}
                            onChange={(e) => setDraft(e.target.value)}
                            placeholder={placeholder}
                            className="w-full bg-black border border-white/10 rounded-sm px-2 py-1.5 text-white placeholder:text-zinc-600 focus:outline-none focus:border-red-500 block"
                        />
                    )}
                    <span className="mt-2 flex justify-between items-center gap-2">
                        <button
                            type="button"
                            onClick={() => { setDraft(""); }}
                            className="text-[10px] text-zinc-500 hover:text-zinc-300"
                            title="Clear (will use built-in default after Save)"
                        >
                            clear (use default)
                        </button>
                        <span className="flex gap-2">
                            <button
                                type="button"
                                onClick={cancel}
                                className="inline-flex items-center gap-1 text-zinc-300 hover:text-white border border-white/10 px-2 py-1 rounded-sm text-[11px]"
                            >
                                <X className="w-3 h-3" /> Cancel
                            </button>
                            <button
                                type="button"
                                onClick={save}
                                disabled={saving}
                                data-testid={`editable-save-${path}`}
                                className="inline-flex items-center gap-1 bg-red-600 hover:bg-red-500 disabled:opacity-50 text-white px-3 py-1 rounded-sm text-[11px] font-bold uppercase tracking-wider"
                            >
                                {saving ? <Loader2 className="w-3 h-3 animate-spin" /> : <Check className="w-3 h-3" />}
                                Save
                            </button>
                        </span>
                    </span>
                </span>
            )}
        </Tag>
    );
}

/**
 * Floating toggle pill that only renders when the server confirms a valid admin
 * Bearer token. A localStorage value alone is NOT enough — we hit /api/admin/verify
 * so random visitors (or someone forging a localStorage entry) never see the pill.
 */
export function EditModeToggle() {
    const { on, setOn } = useEditMode();
    const [verified, setVerified] = useState(false);

    useEffect(() => {
        let cancelled = false;
        const verify = async () => {
            const token = getAdminToken();
            if (!token) {
                if (!cancelled) {
                    setVerified(false);
                    // Make sure edit mode is OFF if there's no token at all.
                    setOn(false);
                }
                return;
            }
            try {
                await adminClient().get("/admin/verify");
                if (!cancelled) setVerified(true);
            } catch {
                if (!cancelled) {
                    setVerified(false);
                    setOn(false);
                }
            }
        };
        verify();
        // Re-verify on storage changes (admin logs in/out in another tab).
        const onStorage = () => verify();
        window.addEventListener("storage", onStorage);
        return () => {
            cancelled = true;
            window.removeEventListener("storage", onStorage);
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    if (!verified) return null;

    return (
        <button
            type="button"
            onClick={() => setOn((v) => !v)}
            data-testid="edit-mode-toggle"
            className={`fixed bottom-24 right-5 z-[9999] inline-flex items-center gap-2 px-4 py-2.5 rounded-full border-2 font-display font-bold uppercase tracking-wider text-xs shadow-2xl transition-colors ${
                on
                    ? "bg-red-600 border-red-400 text-white"
                    : "bg-zinc-950 border-red-600/60 text-red-400 hover:bg-red-950/40"
            }`}
            title={on ? "Click any text to edit it. Click here to exit edit mode." : "Turn on click-to-edit"}
        >
            <Pencil className="w-3.5 h-3.5" />
            {on ? "Editing — tap text" : "Edit site"}
        </button>
    );
}
